/**
 * Тренажёр решений (PULSE-476) — слепое воспроизведение событий на графиках Pulsar.
 * /drill/:token — правила и прогресс по сессиям; /drill/:token/:ord — событие.
 * Будущее отдаёт только сервер и только после «вперёд»; решения фиксируются на открытом краю.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { CG_VIEW_OMIT, MarketChart, type RulerMeasure, type TradeMarkers } from '@/components/market/MarketChart'
import { useDrillEvent, useDrillMutations, useDrillOverview, type DrillAction } from '@/hooks/useDrillApi'

const INK = '#0f121a', CARD = '#131722', LINE = '#2a2e39', DIM = '#787b86', TXT = '#d1d4dc'
const UP = '#089981', DOWN = '#f23645', BLUE = '#2962ff', FIRE = '#FF7A52'
const MONO = 'JetBrains Mono, monospace'
const TFS = ['1m', '3m', '5m', '15m', '1h'] as const
const TF_SEC: Record<string, number> = { '1m': 60, '3m': 180, '5m': 300, '15m': 900, '1h': 3600 }
const REASONS = ['OI', 'Net Delta', 'Объём', 'Ликвидации', 'L/S', 'Фандинг', 'Форма свечи', 'Уровень', 'BTC / рынок',
  'Сильный памп', 'Уже поздно', 'Рано', 'Не нравится монета', 'Другое']

const btn = (bg: string, fg = TXT, extra: CSSProperties = {}): CSSProperties => ({
  background: bg, color: fg, border: `0.5px solid ${LINE}`, borderRadius: 6, padding: '7px 12px',
  fontSize: 13, fontWeight: 600, cursor: 'pointer', ...extra,
})
const chip = (on: boolean): CSSProperties => ({
  background: on ? 'rgba(41,98,255,.18)' : 'transparent', color: on ? '#8fb0ff' : TXT,
  border: `0.5px solid ${on ? BLUE : LINE}`, borderRadius: 14, padding: '3px 10px', fontSize: 12, cursor: 'pointer',
})

function Shell({ children }: { children: ReactNode }) {
  return (
    <div style={{ minHeight: '100vh', background: INK, color: TXT, fontFamily: 'Inter, system-ui, sans-serif', padding: 12, boxSizing: 'border-box' }}>
      <div style={{ width: '100%', maxWidth: 1800, margin: '0 auto' }}>{children}</div>
    </div>
  )
}

const fmtMin = (s: number) => {
  const m = Math.round(s / 60)
  return m < 60 ? `${m} мин` : `${Math.floor(m / 60)} ч ${m % 60 ? `${m % 60} мин` : ''}`.trim()
}

// ─── Главная: правила и прогресс ─────────────────────────────────────
export function DrillHome() {
  const { token } = useParams<{ token: string }>()
  const { data, isLoading, isError } = useDrillOverview(token)
  if (isLoading) return <Shell><div style={{ color: DIM, padding: 40 }}>Загрузка…</div></Shell>
  if (isError || !data) return <Shell><div style={{ color: DOWN, padding: 40 }}>Ссылка не найдена.</div></Shell>
  return (
    <Shell>
      <div style={{ background: CARD, border: `0.5px solid ${LINE}`, borderRadius: 14, padding: '20px 22px', maxWidth: 980 }}>
        <div style={{ fontSize: 22, fontWeight: 600, marginBottom: 6 }}>Тренажёр решений</div>
        <div style={{ color: DIM, marginBottom: 16 }}>{data.name} · пройдено {data.done} из {data.total}</div>
        <div style={{ lineHeight: 1.6, fontSize: 14 }}>
          <p>Перед тобой реальные моменты рынка — монета и дата скрыты, цена показана в процентах (100 = момент события).
            Графики как в CoinGlass: цена, OI (монеты, 100 = на момент события), Net Delta, L/S, ликвидации, фандинг; можно включить тейкеров.</p>
          <p><b>Как это работает.</b> График открыт до момента события. Дальше — кнопки «вперёд» или «играть»:
            будущее открывается свеча за свечой. Назад смотреть можно сколько угодно; вперёд, за открытый край, — нельзя.</p>
          <p><b>Решения</b> — всегда на открытом краю, по последней закрытой цене: шорт / лонг (размер 1–5), жду (чего и сколько), пропуск.
            После входа можно выйти целиком или частично, долить, поставить стоп и цель — они сработают сами, когда откроешь следующие свечи.
            Если передумал — просто прими новое решение: всё пишется в журнал по порядку, ничего не стирается.</p>
          <p><b>Линейка</b> — кнопка «📏 линейка» на графике (или Shift+клик): клик — начало, клик — конец; показывает изменение, число свечей и время. Работает на любой панели.</p>
          <p><b>Почему</b> — отметь причины и уверенность; комментарий — свободно, чем подробнее, тем полезнее.
            Результаты покажем после всего пакета. Сессия — 14 событий, около 15–20 минут; можно прерваться и продолжить.</p>
        </div>
        {data.next_ord && (
          <Link to={`/drill/${token}/${data.next_ord}`} style={{ ...btn(BLUE, '#fff'), display: 'inline-block', textDecoration: 'none', marginTop: 8 }}>
            {data.done ? 'Продолжить' : 'Начать'} → событие {data.next_ord}
          </Link>
        )}
        <div style={{ marginTop: 22, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
          {data.sessions.map((s) => (
            <div key={s.session} style={{ border: `0.5px solid ${LINE}`, borderRadius: 10, padding: 10 }}>
              <div style={{ fontSize: 13, marginBottom: 6 }}>Сессия {s.session} · {s.done}/{s.ords.length}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {s.ords.map((o) => (
                  <Link key={o.ord} to={`/drill/${token}/${o.ord}`} title={`событие ${o.ord}`} style={{
                    width: 26, height: 22, borderRadius: 4, fontSize: 11, display: 'grid', placeItems: 'center', textDecoration: 'none',
                    background: o.status === 'done' ? 'rgba(8,153,129,.25)' : o.status === 'open' ? 'rgba(255,122,82,.2)' : 'transparent',
                    color: TXT, border: `0.5px solid ${LINE}`,
                  }}>{o.ord}</Link>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </Shell>
  )
}

// ─── Событие ──────────────────────────────────────────────────────────
const ACT_LABEL: Record<string, string> = {
  enter: 'вход', add: 'долив', exit: 'выход', wait: 'жду', skip: 'пропуск', set_stop: 'стоп', set_target: 'цель',
  comment: 'комментарий', finish: 'завершил', measure: 'замер', stop_hit: 'сработал стоп', target_hit: 'сработала цель', auto_close: 'закрыто при завершении',
}
function actText(a: DrillAction) {
  const p = a.payload
  const bits: string[] = [ACT_LABEL[a.type] ?? a.type]
  if (p.side) bits.push(p.side === 'short' ? 'шорт' : 'лонг')
  if (p.size) bits.push(`×${p.size}`)
  if (p.fraction && p.fraction < 1) bits.push(`${Math.round(p.fraction * 100)}%`)
  if (p.price != null) bits.push(`@ ${Number(p.price).toFixed(2)}`)
  if (p.wait_for) bits.push(`«${p.wait_for}»`)
  if (p.wait_min) bits.push(`до ${p.wait_min} мин`)
  if (p.confidence) bits.push(`уверенность ${p.confidence}`)
  if (p.reasons?.length) bits.push(`[${p.reasons.join(', ')}]`)
  if (a.type === 'measure') {
    bits.push(`${p.pane ?? ''} ${p.pct != null ? `${p.pct >= 0 ? '+' : ''}${Number(p.pct).toFixed(2)}%` : ''} · ${p.bars} свеч. ${p.tf ?? ''}`)
    return bits.join(' ')
  }
  if (p.comment) bits.push(`— ${p.comment}`)
  return bits.join(' ')
}

export function DrillEvent() {
  const { token, ord: ordS } = useParams<{ token: string; ord: string }>()
  const ord = Number(ordS)
  const nav = useNavigate()
  const [tf, setTf] = useState<string>('5m')
  const [full, setFull] = useState(false)
  const { data, isLoading, isError } = useDrillEvent(token, ord, tf)
  const { reveal, act } = useDrillMutations(token, ord)
  const [playing, setPlaying] = useState(false)
  const [speed, setSpeed] = useState(1000)
  const [size, setSize] = useState(3)
  const [reasons, setReasons] = useState<string[]>([])
  const [conf, setConf] = useState<number | null>(null)
  const [comment, setComment] = useState('')
  const [waitFor, setWaitFor] = useState('')
  const [waitMin, setWaitMin] = useState('')
  const [stopPct, setStopPct] = useState('5')
  const [tgtPct, setTgtPct] = useState('10')
  const [err, setErr] = useState('')
  const busy = useRef(false)

  // что смотрит: таймфрейм и «+ тейкеры» — в журнал (только смены, не первый показ)
  const viewRef = useRef<string>('')
  useEffect(() => {
    const key = `${ord}:${tf}:${full}`
    const prev = viewRef.current
    viewRef.current = key
    if (!prev || !prev.startsWith(`${ord}:`) || !data || data.status === 'done') return
    act.mutate({ type: 'view', payload: { tf, full } })
  }, [tf, full])

  // новое событие — сбросить форму
  useEffect(() => { setPlaying(false); setReasons([]); setConf(null); setComment(''); setWaitFor(''); setWaitMin(''); setErr('') }, [ord])

  const done = data?.status === 'done'
  const atEnd = !!data && data.revealed_s >= data.horizon_s

  const step = async (bars: number) => {
    if (busy.current || !data || done || atEnd) return
    busy.current = true
    try { await reveal.mutateAsync(TF_SEC[tf] * bars) } catch (e: any) { setErr(e?.response?.data?.detail ?? 'ошибка') } finally { busy.current = false }
  }
  useEffect(() => {
    if (!playing) return
    if (done || atEnd) { setPlaying(false); return }
    const id = setInterval(() => { void step(1) }, speed)
    return () => clearInterval(id)
  }, [playing, speed, tf, done, atEnd, data?.revealed_s])

  const send = async (type: string, payload: Record<string, any> = {}, withWhy = true) => {
    setErr('')
    const why = withWhy ? { reasons, confidence: conf ?? undefined, comment: comment || undefined } : {}
    try {
      await act.mutateAsync({ type, payload: { ...why, ...payload } })
      if (withWhy) { setReasons([]); setConf(null); setComment('') }
    } catch (e: any) { setErr(e?.response?.data?.detail ?? 'ошибка') }
  }

  const markers: TradeMarkers | undefined = useMemo(() => {
    if (!data) return undefined
    const pos = data.position
    const entries = data.actions.filter((a) => a.type === 'enter' || a.type === 'add')
    const exits = data.actions.filter((a) => ['exit', 'stop_hit', 'target_hit', 'auto_close'].includes(a.type))
    const tOf = (a: DrillAction) => data.event_t + a.revealed_s - 60
    return {
      direction: pos.side ?? entries[0]?.payload.side ?? 'short',
      signal: { t: data.event_t - 60, price: 100, text: 'событие' },
      entry: entries[0] ? { t: tOf(entries[0]), price: entries[0].payload.price } : null,
      exit: exits.length ? { t: tOf(exits[exits.length - 1]), price: exits[exits.length - 1].payload.price } : null,
      extra: [...entries.slice(1).map((a) => ({ t: tOf(a), price: a.payload.price, text: 'долив', kind: 'add' as const })),
        ...exits.slice(0, -1).map((a) => ({ t: tOf(a), price: a.payload.price, text: 'частично', kind: 'partial' as const }))],
      sl: pos.stop, tp: pos.target,
    }
  }, [data])

  if (isLoading && !data) return <Shell><div style={{ color: DIM, padding: 40 }}>Загрузка события…</div></Shell>
  if (isError || !data) return <Shell><div style={{ color: DOWN, padding: 40 }}>Событие не найдено.</div></Shell>

  const pos = data.position
  const px = data.price_now
  const upnl = pos.side && pos.avg && px ? (pos.side === 'short' ? -1 : 1) * (px / pos.avg - 1) * 100 : null
  const next = ord < data.total ? ord + 1 : null
  const finish = async () => {
    if (pos.side && !window.confirm('Позиция открыта — закрыть по текущей цене и завершить событие?')) return
    await send('finish', {}, true)
    if (next) nav(`/drill/${token}/${next}`); else nav(`/drill/${token}`)
  }

  return (
    <Shell>
      <div style={{ background: CARD, border: `0.5px solid ${LINE}`, borderRadius: 14, overflow: 'hidden' }}>
        {/* шапка */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', padding: '10px 14px', borderBottom: `0.5px solid ${LINE}` }}>
          <Link to={`/drill/${token}`} style={{ color: DIM, textDecoration: 'none', fontSize: 13 }}>← все события</Link>
          <span style={{ fontSize: 16, fontWeight: 600 }}>Событие {ord} <span style={{ color: DIM, fontWeight: 400 }}>из {data.total} · сессия {data.session}</span></span>
          <span style={{ fontFamily: MONO, fontSize: 13, color: data.revealed_s ? FIRE : DIM }}>
            {data.revealed_s ? `+${fmtMin(data.revealed_s)} после события` : 'момент события'}
          </span>
          {px != null && <span style={{ fontFamily: MONO, fontSize: 13 }}>цена {px.toFixed(2)}</span>}
          {done && <span style={{ color: UP, fontSize: 13 }}>завершено — только просмотр</span>}
          <span style={{ marginLeft: 'auto', display: 'inline-flex', gap: 2 }}>
            {TFS.map((v) => (
              <button key={v} onClick={() => setTf(v)} style={{ background: 'transparent', border: 0, cursor: 'pointer', padding: '3px 7px',
                fontSize: 13, fontWeight: 600, color: tf === v ? BLUE : TXT }}>{v}</button>
            ))}
            <button onClick={() => setFull(!full)} style={{ ...chip(full), marginLeft: 8 }}>+ тейкеры</button>
          </span>
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {/* график */}
          <div style={{ flex: '1 1 900px', minWidth: 320 }}>
            <div style={{ height: 'max(640px, calc(100vh - 210px))' }}>
              <MarketChart data={data.series} markers={markers} omit={full ? [] : CG_VIEW_OMIT}
                onMeasure={done ? undefined : (m: RulerMeasure) => void send('measure', { ...m, tf }, false)} />
            </div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', padding: '8px 12px', borderTop: `0.5px solid ${LINE}` }}>
              <button disabled={done || atEnd} onClick={() => setPlaying(!playing)} style={btn(playing ? '#3a2a20' : '#1e222d')}>{playing ? '⏸ пауза' : '▶ играть'}</button>
              <select value={speed} onChange={(e) => setSpeed(Number(e.target.value))} style={{ ...btn('#1e222d'), padding: '6px 8px' }}>
                <option value={2000}>1 свеча / 2 с</option><option value={1000}>1 свеча / с</option><option value={400}>быстро</option>
              </select>
              <button disabled={done || atEnd} onClick={() => step(1)} style={btn('#1e222d')}>+1 свеча</button>
              <button disabled={done || atEnd} onClick={() => step(5)} style={btn('#1e222d')}>+5 свечей</button>
              <button disabled={done || atEnd} onClick={() => step(Math.max(1, Math.round(1800 / TF_SEC[tf])))} style={btn('#1e222d')}>+30 мин</button>
              <span style={{ color: DIM, fontSize: 12 }}>
                {atEnd ? 'достигнут предел (6 ч после события)' : `открыто до +${fmtMin(data.revealed_s)} из ${fmtMin(data.horizon_s)}`}
              </span>
            </div>
          </div>

          {/* решения */}
          <div style={{ flex: '0 1 380px', minWidth: 300, borderLeft: `0.5px solid ${LINE}`, padding: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ border: `0.5px solid ${LINE}`, borderRadius: 8, padding: 10, fontSize: 13 }}>
              {pos.side ? (
                <>
                  <div><b style={{ color: pos.side === 'short' ? DOWN : UP }}>{pos.side === 'short' ? 'ШОРТ' : 'ЛОНГ'}</b> ×{pos.units.toFixed(1)} от <span style={{ fontFamily: MONO }}>{pos.avg?.toFixed(2)}</span></div>
                  {upnl != null && <div style={{ fontFamily: MONO, color: upnl >= 0 ? UP : DOWN }}>текущий {upnl >= 0 ? '+' : ''}{upnl.toFixed(2)}%</div>}
                  <div style={{ color: DIM }}>стоп {pos.stop ? pos.stop.toFixed(2) : '—'} · цель {pos.target ? pos.target.toFixed(2) : '—'}</div>
                </>
              ) : <div style={{ color: DIM }}>Позиции нет{pos.decided ? ` · последнее решение: ${ACT_LABEL[pos.decided]}` : ''}</div>}
              {pos.realized !== 0 && <div style={{ fontFamily: MONO, color: DIM }}>закрыто: {pos.realized >= 0 ? '+' : ''}{pos.realized.toFixed(2)} (%×размер)</div>}
            </div>

            {!done && (
              <>
                {!pos.side ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ color: DIM, fontSize: 12 }}>размер</span>
                      {[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setSize(n)} style={chip(size === n)}>{n}</button>)}
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => send('enter', { side: 'short', size })} style={btn('rgba(242,54,69,.25)', '#fff', { flex: 1 })}>Шорт</button>
                      <button onClick={() => send('enter', { side: 'long', size })} style={btn('rgba(8,153,129,.25)', '#fff', { flex: 1 })}>Лонг</button>
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input value={waitFor} onChange={(e) => setWaitFor(e.target.value)} placeholder="жду чего (необязательно)" style={inp({ flex: 1 })} />
                      <input value={waitMin} onChange={(e) => setWaitMin(e.target.value.replace(/\D/g, ''))} placeholder="мин" style={inp({ width: 54 })} />
                    </div>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => send('wait', { wait_for: waitFor, wait_min: waitMin ? Number(waitMin) : undefined })} style={btn('#1e222d', TXT, { flex: 1 })}>Жду</button>
                      <button onClick={() => send('skip')} style={btn('#1e222d', TXT, { flex: 1 })}>Пропускаю</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => send('exit', { fraction: 1 })} style={btn('#1e222d', TXT, { flex: 1 })}>Закрыть всё</button>
                      <button onClick={() => send('exit', { fraction: 0.5 })} style={btn('#1e222d', TXT, { flex: 1 })}>Закрыть 50%</button>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ color: DIM, fontSize: 12 }}>долить</span>
                      {[1, 2, 3].map((n) => <button key={n} onClick={() => send('add', { size: n })} style={chip(false)}>+{n}</button>)}
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ color: DIM, fontSize: 12, width: 44 }}>стоп</span>
                      <input value={stopPct} onChange={(e) => setStopPct(e.target.value)} style={inp({ width: 56 })} />
                      <span style={{ color: DIM, fontSize: 12 }}>% от цены</span>
                      <button onClick={() => send('set_stop', { pct: Number(stopPct) }, false)} style={btn('#1e222d', TXT, { marginLeft: 'auto' })}>поставить</button>
                    </div>
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ color: DIM, fontSize: 12, width: 44 }}>цель</span>
                      <input value={tgtPct} onChange={(e) => setTgtPct(e.target.value)} style={inp({ width: 56 })} />
                      <span style={{ color: DIM, fontSize: 12 }}>% от цены</span>
                      <button onClick={() => send('set_target', { pct: Number(tgtPct) }, false)} style={btn('#1e222d', TXT, { marginLeft: 'auto' })}>поставить</button>
                    </div>
                  </div>
                )}

                <div>
                  <div style={{ color: DIM, fontSize: 12, marginBottom: 6 }}>почему (к следующему решению)</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {REASONS.map((r) => (
                      <button key={r} onClick={() => setReasons(reasons.includes(r) ? reasons.filter((x) => x !== r) : [...reasons, r])} style={chip(reasons.includes(r))}>{r}</button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 8 }}>
                    <span style={{ color: DIM, fontSize: 12 }}>уверенность</span>
                    {[1, 2, 3, 4, 5].map((n) => <button key={n} onClick={() => setConf(conf === n ? null : n)} style={chip(conf === n)}>{n}</button>)}
                  </div>
                  <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} placeholder="комментарий: что видишь, чего ждёшь, что смущает"
                    style={{ ...inp({ width: '100%', marginTop: 8, resize: 'vertical' }), boxSizing: 'border-box' }} />
                  <button disabled={!comment.trim()} onClick={() => send('comment')} style={btn('#1e222d', TXT, { marginTop: 6 })}>Сохранить комментарий</button>
                </div>

                <button onClick={finish} style={btn(BLUE, '#fff')}>Завершить событие{next ? ' → следующее' : ''}</button>
              </>
            )}
            {done && next && <Link to={`/drill/${token}/${next}`} style={{ ...btn(BLUE, '#fff'), textDecoration: 'none', textAlign: 'center' }}>Следующее событие →</Link>}
            {err && <div style={{ color: DOWN, fontSize: 13 }}>{err}</div>}

            <div>
              <div style={{ color: DIM, fontSize: 12, marginBottom: 4 }}>журнал события</div>
              <div style={{ maxHeight: 260, overflow: 'auto', fontSize: 12, lineHeight: 1.5 }}>
                {data.actions.length === 0 && <div style={{ color: DIM }}>пока пусто</div>}
                {data.actions.filter((a) => a.type !== 'view').map((a) => (
                  <div key={a.id}><span style={{ fontFamily: MONO, color: DIM }}>+{fmtMin(a.revealed_s)}</span> {actText(a)}</div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Shell>
  )
}

function inp(extra: CSSProperties = {}): CSSProperties {
  return { background: '#1e222d', color: TXT, border: `0.5px solid ${LINE}`, borderRadius: 6, padding: '6px 8px', fontSize: 13, ...extra }
}
